/* eslint-disable no-nested-ternary */
import moment from 'moment';
import React from 'react';
import { showAlert, useAuth, User } from '../../../../context/authContext';
import {
  CashRegister,
  onItemDelete,
  storePreferenceFormAction,
} from '../../../../context/storeContext';
import { PrefFormActions } from '../../../../pages/Preferences/Preferences';
import { formatErrors } from '../../../../utils/request';
import Icon from '../../../Icon/Icon';
import DeletePrompt from '../../../Modal/DeletePrompt/DeletePrompt';
import RegisterAdd from '../../../Modal/RegisterAdd/RegisterAdd';

type RegistersTabProps = {
  user: User;
};

const RegistersTab = React.forwardRef<PrefFormActions, RegistersTabProps>(
  ({ user }, ref) => {
    const [{ storePreferences }, dispatch] = useAuth();
    const [cashRegisters, setCashRegisters] = React.useState<CashRegister[]>(
      [],
    );
    const [register, setRegister] = React.useState<CashRegister>();
    const [deleteID, setDeleteID] = React.useState<number>();
    const [isMakingDefault, setIsMakingDefault] = React.useState<number>();
    const [showAddRegisterModal, setShowAddRegisterModal] =
      React.useState(false);

    React.useImperativeHandle(ref, () => {
      return {
        fnActionCall: () => false,
        fnShowModalCall: setShowAddRegisterModal,
      };
    });

    React.useEffect(() => {
      if (storePreferences?.cashRegisters) {
        setCashRegisters(storePreferences.cashRegisters);
      }
    }, [storePreferences?.cashRegisters]);

    const handleRegisterAdd = async (newRegister: {
      name: string;
      description: string;
      isPrimary: boolean;
    }) => {
      if (!user?.defaultStore) {
        return;
      }

      try {
        const { cashRegister: getCashRegister } =
          (await storePreferenceFormAction(
            `/cash-register?store=${user.defaultStore.id}`,
            !register ? 'post' : 'put',
            {
              ...newRegister,
              storeId: user.defaultStore.id,
              ...(register && { cashRegisterId: register.id }),
            },
          )) as any;

        if (storePreferences && getCashRegister) {
          dispatch({
            type: 'SET_STORE_PREFERENCES',
            payload: {
              ...storePreferences,
              cashRegisters: register
                ? storePreferences.cashRegisters.map(fndRegister => {
                    if (fndRegister.id === register.id) {
                      return getCashRegister;
                    }

                    return {
                      ...fndRegister,
                      isPrimary: newRegister.isPrimary
                        ? false
                        : fndRegister.isPrimary,
                    };
                  })
                : storePreferences.cashRegisters.length > 0
                ? [
                    getCashRegister,
                    ...storePreferences.cashRegisters.map(fndRegister => ({
                      ...fndRegister,
                      isPrimary: newRegister.isPrimary
                        ? false
                        : fndRegister.isPrimary,
                    })),
                  ]
                : [getCashRegister],
            },
          });
        }

        setShowAddRegisterModal(false);
        setRegister(undefined);
      } catch (error) {
        const { message } = formatErrors(error);
        showAlert(dispatch, {
          show: true,
          message,
          type: 'ERROR',
        });
      }
    };

    const handleMakeDefault = async (fndCashRegister: CashRegister) => {
      if (!user?.defaultStore?.id || fndCashRegister.isPrimary) {
        return;
      }

      try {
        setIsMakingDefault(fndCashRegister.id);

        (await storePreferenceFormAction(
          `/cash-register?store=${user?.defaultStore?.id}`,
          'put',
          {
            name: fndCashRegister.name,
            description: fndCashRegister.description,
            isPrimary: true,
            storeId: user?.defaultStore?.id,
            cashRegisterId: fndCashRegister.id,
          },
        )) as any;

        if (storePreferences) {
          dispatch({
            type: 'SET_STORE_PREFERENCES',
            payload: {
              ...storePreferences,
              cashRegisters: storePreferences.cashRegisters.map(
                fndRegister => ({
                  ...fndRegister,
                  isPrimary: fndRegister.id === fndCashRegister.id,
                }),
              ),
            },
          });
        }

        setIsMakingDefault(undefined);
      } catch (error) {
        setIsMakingDefault(undefined);
        const { message } = formatErrors(error);
        showAlert(dispatch, {
          show: true,
          message,
          type: 'ERROR',
        });
      }
    };

    return (
      <>
        <div className="w-full mt-10">
          <div className="bg-white w-[80%] mx-auto rounded-md border border-[rgba(216,222,228,0.8)]">
            <table className="border-collapse table-auto w-full h-full bg-white table-striped rounded-md relative">
              <thead>
                <tr className="text-left !bg-pos-header-bg rounded-tr-md border-b !border-[rgba(216,222,228,0.8)] sticky top-0 z-10 h-[44.59px]">
                  <th className="!text-xbase font-semibold !border-[rgba(216,222,228,0.8)]">
                    #
                  </th>
                  <th className="!text-xbase font-semibold !border-[rgba(216,222,228,0.8)]">
                    Name
                  </th>
                  <th className="!text-xbase font-semibold !border-[rgba(216,222,228,0.8)]">
                    Default
                  </th>
                  <th className="!text-xbase font-semibold !border-[rgba(216,222,228,0.8)] w-[20%]">
                    Date Created
                  </th>
                  <th scope="col" className="w-[20%]">
                    &nbsp;
                  </th>
                </tr>
              </thead>
              <tbody className="relative">
                {cashRegisters.length === 0 ? (
                  <tr className="w-full h-full bg-[#f2f2f2] bg-opacity-75 transition-opacity">
                    <td
                      colSpan={5}
                      rowSpan={4}
                      className="!border-none text-center"
                    >
                      <span className="text-pos-black test-sm font-medium">
                        No registers found!
                      </span>
                    </td>
                  </tr>
                ) : (
                  cashRegisters.map((fndRegister, index) => (
                    <tr key={fndRegister.id}>
                      <td>{index + 1}</td>
                      <td>{fndRegister.name}</td>
                      <td>{fndRegister.isPrimary ? 'Yes' : 'No'}</td>
                      <td>
                        {moment(fndRegister.createdAt).format(
                          'DD-MM-YYYY h:mm A',
                        )}
                      </td>
                      <td>
                        <div className="flex flex-row items-center justify-end space-x-8">
                          <button
                            onClick={() => setRegister(fndRegister)}
                            type="button"
                            className="w-[16px] h-[16px] transform hover:scale-110"
                          >
                            <Icon
                              id="iconTableEdit"
                              className="w-[16px] h-[16px] fill-pos-gray"
                            />
                          </button>
                          <button
                            type="button"
                            className={`${
                              fndRegister.isPrimary ? 'invisible' : ''
                            } w-[16px] h-[16px] transform hover:scale-110`}
                          >
                            <Icon
                              onClick={() => setDeleteID(fndRegister.id)}
                              id="iconTableDelete"
                              className="w-[16px] h-[16px] fill-pos-gray"
                            />
                          </button>
                          <button
                            onClick={() =>
                              isMakingDefault === fndRegister.id
                                ? undefined
                                : handleMakeDefault(fndRegister)
                            }
                            type="button"
                            className={`${
                              fndRegister.isPrimary ? 'invisible' : ''
                            } !ml-[25px] text-xbase ${
                              isMakingDefault !== fndRegister.id
                                ? 'text-pos-hyperlink underline'
                                : ''
                            } transform hover:scale-110`}
                          >
                            {isMakingDefault === fndRegister.id
                              ? 'Please wait...'
                              : 'Make Default'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {(showAddRegisterModal || register) && (
          <RegisterAdd
            cashRegister={register}
            onAddRegister={handleRegisterAdd}
            onCloseModal={() => {
              setShowAddRegisterModal(false);
              setRegister(undefined);
            }}
          />
        )}

        {deleteID && user?.defaultStore?.id && storePreferences && (
          <DeletePrompt
            showArchive={false}
            onCloseModal={() => setDeleteID(undefined)}
            onRemoveItem={() => {
              dispatch({
                type: 'SET_STORE_PREFERENCES',
                payload: {
                  ...storePreferences,
                  cashRegisters: storePreferences.cashRegisters.filter(
                    fndRegister => fndRegister.id !== deleteID,
                  ),
                },
              });
            }}
            deleteCb={async () =>
              onItemDelete(`/cash-register?store=${user?.defaultStore?.id}`, {
                cashRegisterId: deleteID,
              })
            }
          />
        )}
      </>
    );
  },
);

export default RegistersTab;
